// src/features/channelManager/api/channelsApi.js
import axios from "axios";

/**
 * Single API wrapper for Channel Manager
 * - one baseURL
 * - auth header interceptor
 * - keeps current endpoints
 * - adds booking dashboard endpoints
 */

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL, // e.g. https://api.netshineoffice.com/api
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const channelsApi = {
  // Channels master + company connections
  listCompanyChannels: (params = {}) => api.get("/channels/list", { params }),

  // OTA validate + room/rate discovery
  testConnection: (payload) => api.post("/channels/test-connection", payload),
  mappingDetails: (payload) => api.post("/channels/mapping-details", payload),

  // Main connect/update endpoint
  connect: (payload) => api.post("/channels/connects", payload),

  // Channel actions
  disconnect: (payload) => api.post("/channels/disconnect", payload),
  reconnect: (payload) => api.post("/channels/reconnect", payload),
  setActive: (payload) => api.post("/channels/set-company-channel-active", payload),
  reconcile: (payload) => api.post("/channels/reconcile-company-channel", payload),

  // Booking sync
  syncNow: (payload) => api.post("/channels/sync-booking-revisions", payload),
  syncBookingRevisions: (payload) => api.post("/channels/sync-booking-revisions", payload),

  // Embedded setup token
  iframeToken: (payload) => api.post("/channels/iframe-token", payload),

  // ARI
  ariBulkUpdate: (payload) => api.post("/channels/ari/bulk-update", payload),
  ariGrid: (params) => api.get("/channels/ari/grid", { params }),
  verifyChannexAri: (params) => api.get("/channels/ari/channex-verify", { params }),
  ariSave: (payload) => api.post("/channels/ari/save", payload),

  // PMS
  listPmsRoomTypes: () => api.get("/roomtype/listings/"),

  // Booking Engine read-only data used by the Channel Manager UI preview.
  // These endpoints already exist; the preview does not add or change backend behavior.
  bookingEngineRateOptions: (roomTypeId) =>
    api.get("/booking-engine-settings/manual-rate-options", { params: { roomTypeId } }),
  bookingEngineManualCalendar: ({ roomTypeId, startDate, endDate }) =>
    api.get("/booking-engine-settings/manual-calendar", {
      params: { roomTypeId, startDate, endDate },
    }),
  bookingEngineSaveManualCalendar: (payload) =>
    api.post("/booking-engine-settings/manual-calendar", payload),
  bookingEnginePricingSettings: () =>
    api.get("/booking-engine-settings/pricing-settings"),
  bookingEngineSavePricingSettings: (payload) =>
    api.put("/booking-engine-settings/pricing-settings", payload),

  // Legacy/current endpoints - keep for now if routes still exist
  getBookings: (params = {}) => api.get("/channels/bookings", { params }),
  pushAvailability: (payload) => api.post("/channels/availability", payload),

  // Rate plans
  createRatePlan: (payload) => api.post("/channels/rate-plans", payload),
  updateRatePlan: (ratePlanId, payload) =>
    api.put(`/channels/rate-plans/${ratePlanId}`, payload),

  // Booking dashboard - new
  bookingDashboardSummary: (params = {}) =>
    api.get("/channels/booking-dashboard/summary", { params }),

  listBookingRevisions: (params = {}) =>
    api.get("/channels/booking-revisions", { params }),

  getBookingRevision: (revisionId) =>
    api.get(`/channels/booking-revisions/${revisionId}`),

  acknowledgeBookingRevision: (revisionId, payload = {}) =>
    api.post(`/channels/booking-revisions/${revisionId}/ack`, payload),

  retryFailedAcks: (payload = {}) =>
    api.post("/channels/booking-revisions/retry-acks", payload),

  importBookingRevision: (revisionId, payload = {}) =>
    api.post(`/channels/booking-revisions/${revisionId}/import`, payload),
};

export default api;